import { apiClient } from '../api/apiClient';

export interface SendDietPlanResponse {
  message_id: number;
  conversation_id: number;
  provider_message_id?: string;
  status: string;
}

export interface MessagingConversationRead {
  id: number;
  channel: string;
  patient_user_id: number | null;
  doctor_id: number | null;
  system_conversation: boolean;
  last_message_at: string | null;
  created_at: string;
  message_count?: number;
}

export interface MessagingConversationList {
  items: MessagingConversationRead[];
  total: number;
  skip: number;
  limit: number;
}

export interface MessagingMessageRead {
  id: number;
  conversation_id: number;
  direction: string;
  channel: string;
  provider_message_id: string | null;
  from_phone_e164: string | null;
  to_phone_e164: string | null;
  body: string | null;
  media_urls: string[] | null;
  template_name: string | null;
  sender_type: string;
  sender_doctor_id: number | null;
  sender_user_id: number | null;
  status: string;
  error_code: string | null;
  error_message: string | null;
  cost_amount: number | null;
  cost_currency: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  created_at: string;
  /** For diet_plan_ready messages: plan_id and version_id for resend. */
  metadata?: { plan_id?: string; version_id?: string } | null;
}

/**
 * Send the current meal plan PDF to the patient via WhatsApp using the registered diet plan template.
 * Requires TWILIO_WHATSAPP_TEMPLATE_DIET_PLAN_CONTENT_SID and patient WhatsApp opt-in.
 */
/** List conversations for the current doctor; optionally filter by patient user id. */
export async function getConversations(params?: {
  skip?: number;
  limit?: number;
  patient_user_id?: number;
}): Promise<MessagingConversationList> {
  const res = await apiClient.get<MessagingConversationList>('/api/v1/messaging/conversations', {
    params: { skip: params?.skip ?? 0, limit: params?.limit ?? 50, patient_user_id: params?.patient_user_id },
  });
  return res.data;
}

/** List messages in a conversation (newest first by created_at). */
export async function getConversationMessages(
  conversationId: number,
  params?: { skip?: number; limit?: number }
): Promise<MessagingMessageRead[]> {
  const res = await apiClient.get<MessagingMessageRead[]>(
    `/api/v1/messaging/conversations/${conversationId}/messages`,
    { params: { skip: params?.skip ?? 0, limit: params?.limit ?? 100 } }
  );
  return res.data;
}

/** Get presigned download URLs for a message's media (for Messages UI). */
export async function getMessageMedia(
  messageId: number,
  expiresIn: number = 3600
): Promise<{ urls: string[] }> {
  const res = await apiClient.get<{ urls: string[] }>(
    `/api/v1/messaging/messages/${messageId}/media`,
    { params: { expires_in: expiresIn } }
  );
  return res.data;
}

export async function sendDietPlanViaWhatsApp(
  planId: string,
  versionId: string
): Promise<SendDietPlanResponse> {
  const res = await apiClient.post<SendDietPlanResponse>('/api/v1/messaging/send-diet-plan', {
    plan_id: planId,
    version_id: versionId,
  });
  return res.data;
}
